import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Select } from '../ui/newSelect';
import { BILLING_CURRENCY_OPTIONS } from '../utils/agreementLineItemHelpers';
import { Actions as AGREEMENTS } from '../../store/actions/agreementsActions';

/**
 * Public consultant self-signup. Creates a Consultant master Agreement; after
 * the applicant accepts the schedule, a Platform Admin must Confirm before
 * consultant admin rights are granted.
 */
const ConsultantSignupPage = ({ createConsultantSelfSignup }) => {
  const [form, setForm] = useState({
    customerOrganisationName: '',
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    billingCurrency: '£',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [agreementUrl, setAgreementUrl] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.customerOrganisationName.trim() || !form.customerEmail.trim()) {
      setError('Enter your organisation name and work email.');
      return;
    }
    if (!form.customerFirstName.trim() || !form.customerLastName.trim()) {
      setError('Enter your name.');
      return;
    }
    setSaving(true);
    const result = await createConsultantSelfSignup({
      customerOrganisationName: form.customerOrganisationName.trim(),
      customerFirstName: form.customerFirstName.trim(),
      customerLastName: form.customerLastName.trim(),
      customerEmail: form.customerEmail.trim(),
      billingCurrency: form.billingCurrency,
    });
    setSaving(false);
    if (result.ok && result.data?.agreementUrl) {
      setAgreementUrl(result.data.agreementUrl);
    } else {
      setError(result.error || 'Could not start signup. Please try again or contact sales.');
    }
  };

  return (
    <div className='min-h-screen bg-background text-foreground flex items-center justify-center p-6'>
      <Card className='w-full max-w-lg bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle>Join as a consultancy</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {agreementUrl ? (
            <div className='space-y-3'>
              <Alert>
                <AlertDescription className='text-sm'>
                  Your Consultant Pricing Schedule is ready to review. Accept it to record your signature. A Compare
                  Your Footprint admin will Confirm your application before you can add clients.
                </AlertDescription>
              </Alert>
              <Button onClick={() => window.open(agreementUrl, '_blank', 'noopener')}>
                Review and accept agreement
              </Button>
            </div>
          ) : (
            <form className='space-y-3' onSubmit={onSubmit}>
              <Alert>
                <AlertDescription className='text-xs'>
                  Self-serve starts with no annual portfolio minimum and Foundation pricing. Sales can later set a
                  minimum and starting level on a replacement agreement if needed.
                </AlertDescription>
              </Alert>
              <div>
                <Label>Organisation name *</Label>
                <Input
                  value={form.customerOrganisationName}
                  onChange={(e) => update('customerOrganisationName', e.target.value)}
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>First name *</Label>
                  <Input value={form.customerFirstName} onChange={(e) => update('customerFirstName', e.target.value)} />
                </div>
                <div>
                  <Label>Last name *</Label>
                  <Input value={form.customerLastName} onChange={(e) => update('customerLastName', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Work email *</Label>
                <Input
                  type='email'
                  value={form.customerEmail}
                  onChange={(e) => update('customerEmail', e.target.value)}
                />
              </div>
              <div>
                <Label>Billing currency</Label>
                <Select
                  value={form.billingCurrency}
                  onValueChange={(v) => update('billingCurrency', v)}
                  options={BILLING_CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                />
              </div>
              {error && (
                <Alert variant='destructive'>
                  <AlertDescription className='text-xs'>{error}</AlertDescription>
                </Alert>
              )}
              <Button type='submit' loading={saving} className='w-full'>
                Continue to agreement
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  createConsultantSelfSignup: (body) => dispatch(AGREEMENTS.createConsultantSelfSignup(body)),
});

export default connect(null, mapDispatchToProps)(ConsultantSignupPage);
